import MentionTextarea from './MentionTextarea';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { api } from '../lib/api';
import { POST_TYPES, TYPE_BY_KEY } from '../lib/requests';
import Icon from './Icon';

/* Not an empty box: six intents, each nudging toward something concrete. A
   Post shares; the other five are requests and land on Open Requests. */

const Composer = forwardRef(function Composer({ onPosted }, ref) {
  // Bumped to move the cursor into the box (Tour, answering a prompt).
  const [open, setOpen] = useState(0);
  const [kind, setKind] = useState('post');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Set when the composer was opened by answering the week's prompt. Stamped
  // onto the post so answers can be counted and found later.
  const [prompt, setPrompt] = useState(null);
  // Referral posts: the company, an optional reward, tags and a job link.
  const [refer, setRefer] = useState({ company: '', reward_amount: '', reward_terms: '', tags: '', link: '' });

  useImperativeHandle(ref, () => ({
    open: (answering = null) => {
      setPrompt(answering);
      setOpen(Date.now());
    },
  }));

  const type = TYPE_BY_KEY[kind];
  const ready = kind === 'refer'
    ? title.trim().length >= 3 && refer.company.trim().length >= 2
    : type.request
    ? title.trim().length >= 3
    : body.trim().length > 1;

  async function submit() {
    setBusy(true);
    setError('');
    try {
      const made = await api.createPost({
        kind,
        // The headline box is hidden for a plain Post: don't send what's no longer on screen.
        title: type.request ? title.trim() : '',
        body: body.trim(),
        fields: kind === 'refer' ? { ...refer, tags: refer.tags } : (prompt ? { prompt: prompt.key } : {}),
      });
      setRefer({ company: '', reward_amount: '', reward_terms: '', tags: '', link: '' });
      if (made?.state === 'pending') {
        setKind('post'); setTitle(''); setBody(''); setPrompt(null);
        onPosted?.(true, 'Sent for approval — an admin checks referral rewards before they go live');
        return;
      }
      setKind('post');
      setTitle('');
      setBody('');
      setPrompt(null);
      onPosted?.(type.request);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setKind('post'); setTitle(''); setBody(''); setPrompt(null); setError('');
    setRefer({ company: '', reward_amount: '', reward_terms: '', tags: '', link: '' });
  }

  // Always open, like the design: the kinds, the question, the box. `open`
  // (from the Tour or an Answer prompt) just puts the cursor in it.
  return (
    <div className="card composer composer-always" data-tour="compose">
      {prompt && (
        <p className="composer-answering">
          Answering: <b>{prompt.text}</b>
          <button className="linklike" onClick={() => setPrompt(null)}>clear</button>
        </p>
      )}
      <div className="composer-open">
        <div className="composer-kinds" role="radiogroup" aria-label="What kind of post">
          {POST_TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              role="radio"
              aria-checked={kind === t.key}
              className={`fchip kind-chip k-${t.key}${kind === t.key ? ' on' : ''}`}
              onClick={() => setKind(t.key)}
            >
              <Icon name={t.icon} size={15} /> {t.label.replace('…', '')}
            </button>
          ))}
        </div>
        <h2 className="composer-q">{kind === 'post' ? 'What’s on your mind today?' : type.tagline}</h2>

        {type.request && (
          <input
            className="in" value={title} maxLength={90}
            placeholder={type.headline}
            aria-label="Headline"
            onChange={(e) => setTitle(e.target.value)}
          />
        )}
        {kind === 'refer' && (
          <div className="refer-fields">
            <input className="in" value={refer.company} maxLength={80} placeholder="Company or team hiring (required)"
              aria-label="Company" onChange={(e) => setRefer({ ...refer, company: e.target.value })} />
            <div className="refer-row">
              <input className="in" value={refer.reward_amount} inputMode="numeric" placeholder="Referral reward in USD (optional)"
                aria-label="Referral reward" onChange={(e) => setRefer({ ...refer, reward_amount: e.target.value })} />
              <input className="in" value={refer.tags} placeholder="Tags, e.g. Engineering, Remote, Web3"
                aria-label="Tags" onChange={(e) => setRefer({ ...refer, tags: e.target.value })} />
            </div>
            {refer.reward_amount && (
              <input className="in" value={refer.reward_terms} maxLength={200} placeholder="When it's paid, e.g. if hired and stays 90 days"
                aria-label="Reward conditions" onChange={(e) => setRefer({ ...refer, reward_terms: e.target.value })} />
            )}
            <input className="in" value={refer.link} placeholder="Job link (optional, https://…)"
              aria-label="Job link" onChange={(e) => setRefer({ ...refer, link: e.target.value })} />
            {refer.reward_amount && <p className="muted-sm">Posts with a cash reward are checked by an admin before they go live. The company pays the reward; B/N tracks it.</p>}
          </div>
        )}
        <MentionTextarea
          className="ta" value={body} focusKey={open}
          placeholder={kind === 'post' ? 'What are you working on or need help with? (type @ to tag a connection)' : `${type.body} (type @ to tag a connection)`}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); if (ready) submit(); } }}
        />
        {type.request && <p className="composer-tagline">Shows on your Open Requests until you mark it solved.</p>}

        {error && <div className="pe-error">{error}</div>}

        <div className="composer-actions">
          <button className="btn quiet" onClick={reset} disabled={busy || (!title && !body && kind === 'post')}>Cancel</button>
          <button className="btn apply composer-post" onClick={submit} disabled={!ready || busy}>
            {busy ? 'Posting…' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default Composer;
